import { BusinessObjectBase } from './base';
import { tokenHistorySchema } from '@schemas/business-objects/token-history';
import type { Tenant } from './tenant';
import type { Token } from './token';
import type { HistoryData, Id, TokenHistoryEventType } from '../base-types';

export interface TokenHistoryData {
  readonly id: Id;
  readonly eventType: TokenHistoryEventType;
  readonly activityId?: string;
  readonly data?: HistoryData;
  readonly timestamp?: Date;
  readonly tokenId: Id;
  readonly token?: Token;
  readonly tenantId: Id;
  readonly tenant?: Tenant;
}

export interface TokenHistory extends Readonly<TokenHistoryData> {}
type TokenHistoryCreateWithChildrenData = Partial<TokenHistoryData>;

export class TokenHistory extends BusinessObjectBase {
  static readonly entityName = 'TokenHistory' as const;
  static readonly plural = 'token-histories';
  static schema = tokenHistorySchema;

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<TokenHistory[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new TokenHistory(data));
  }

  static async findOne(filter?: any): Promise<TokenHistory | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new TokenHistory(data) : null;
  }

  static async findById(id: Id, filter?: any): Promise<TokenHistory> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new TokenHistory(data);
  }

  static async create(
    data: Omit<TokenHistoryData, 'id'>,
  ): Promise<TokenHistory> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new TokenHistory(result);
  }

  static async createMany(
    data: Omit<TokenHistoryData, 'id'>[],
  ): Promise<TokenHistory[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new TokenHistory(item));
  }

  static async createWithChildren(
    data: TokenHistoryCreateWithChildrenData,
  ): Promise<TokenHistory> {
    const url = `${this.baseUrl}/${this.plural}/create-with-children`;
    const result = await this.post<any>(url, data);
    return new TokenHistory(result);
  }

  static async createManyWithChildren(
    data: TokenHistoryCreateWithChildrenData[],
  ): Promise<TokenHistory[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many-with-children`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new TokenHistory(item));
  }

  static async updateById(
    id: Id,
    updates: Partial<TokenHistoryData>,
  ): Promise<TokenHistory> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const result = await this.patch<any>(url, updates);
    return new TokenHistory(result);
  }

  static async deleteById(id: Id): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }
}
